import { ApolloClient, HttpLink, InMemoryCache, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { datadogLogs } from "@datadog/browser-logs";

import { discoverCaaEndpoints } from "@repo/shopify/utils/caaDiscovery";

import { SHOPIFY_DOMAIN } from "@const/urls";

import { refreshHikoCaaToken } from "@lib/hikoHeadlessSession";

import type { ICaaToken } from "@store/customer";
import { useCustomerStore } from "@store/customer";

/**
 * Customer Account API 전용 Apollo client.
 * jp locale에서만 사용 — 기존 `lib/apollo.ts`의 싱글턴 client(Storefront API)는 그대로 유지
 */

const JP_SHOP_DOMAIN = SHOPIFY_DOMAIN.jp.replace(/^https?:\/\//, "").replace(/\/$/, "");

const CAA_CLIENT_ID = import.meta.env.VITE_JP_CAA_CLIENT_ID;

// access_token 만료 60초 전부터는 미리 갱신
const EXPIRY_BUFFER_MS = 60_000;

// 동시에 여러 요청이 만료 임박을 감지해도 refresh 호출은 한 번만 나가도록 in-flight 요청을 공유
let inFlightRefresh: Promise<ICaaToken> | null = null;

const refreshCaaToken = (refreshToken: string): Promise<ICaaToken> => {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    const { tokenEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        client_id: CAA_CLIENT_ID,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      // refresh_token 만료/폐기 등으로 갱신 실패 시 store를 정리 — 무효한 토큰으로 매 요청마다 재시도되는 것을 방지
      useCustomerStore.getState().logout();
      throw new Error(`[CAA] token refresh failed: ${response.status}`);
    }

    const data = (await response.json()) as {
      access_token: string;
      refresh_token: string;
      id_token: string;
      expires_in: number;
    };

    const caaToken: ICaaToken = {
      accessToken: data.access_token,
      // Shopify가 refresh 시 새 refresh_token을 내려줄 수 있어 매번 덮어씌움
      refreshToken: data.refresh_token,
      idToken: data.id_token,
      expiresAt: new Date(Date.now() + data.expires_in * 1000).toISOString(),
    };

    useCustomerStore.getState().loginCaa(caaToken);

    return caaToken;
  })().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      datadogLogs.logger.error("[APOLLO][CAA] GraphQL error", {
        message,
        location: JSON.stringify(locations),
        path: JSON.stringify(path),
      });
    });
  }

  if (networkError) {
    datadogLogs.logger.error("[APOLLO][CAA] Network error", { networkError });
    // onError 링크 안에서 throw하면 정상적인 Promise rejection 경로를 벗어나 uncaught 에러로
    // 터진다 — 401(access token 만료/revoked) 같은 상황에서 호출부의 .catch(handleSessionExpired)
    // 등이 아예 못 받고 페이지가 크래시했다. Apollo가 networkError 자체로 이미 쿼리의 Promise를
    // reject시키므로 여기선 로깅만 하고 그대로 흘려보낸다.
  }
});

/**
 * 만료 임박(60초 이내)이면 갱신 후 반환, 아니면 현재 caaToken을 그대로 반환.
 * apolloCaaClient(authLink)뿐 아니라, caaToken을 직접 꺼내 Storefront API에 넘겨야 하는 곳(App.tsx의 cart buyerIdentity 동기화)에서도 만료된 토큰을 그대로 보내는 일이 없도록 함
 */
export const getFreshCaaToken = async (): Promise<ICaaToken | null> => {
  let { caaToken } = useCustomerStore.getState();

  if (caaToken && new Date(caaToken.expiresAt).getTime() - Date.now() < EXPIRY_BUFFER_MS) {
    // "hiko" 세션은 refresh_token을 직접 보유하지 않으므로 위젯에 갱신을 위임
    caaToken = caaToken.source === "hiko" ? await refreshHikoCaaToken() : await refreshCaaToken(caaToken.refreshToken);
  }

  return caaToken;
};

const authLink = setContext(async () => {
  const { graphqlApiEndpoint } = await discoverCaaEndpoints(JP_SHOP_DOMAIN);

  const caaToken = await getFreshCaaToken();

  return {
    uri: graphqlApiEndpoint,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(caaToken?.accessToken ? { Authorization: caaToken.accessToken } : {}),
    },
  };
});

// 실제 요청 URL은 authLink가 context.uri로 매 요청마다 덮어씌움 (discovery 완료 전엔 사용되지 않음)
const httpLink = new HttpLink({ uri: "https://shopify.com/" });

const apolloCaaClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([authLink, errorLink, httpLink]),
});

export default apolloCaaClient;
